import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { AppointmentCustomerDTO, EventRsvpCustomerDTO } from "@gymflow/types";
import { DotBadge } from "apps/portal/src/components/atoms/DotBadge";
import { LocationMarkerIcon } from "apps/portal/src/components/atoms/icons/LocationMarkerIcon";
import { StaffAvatar } from "apps/portal/src/components/molecules/StaffAvatar";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes";
import { DateTime, Interval } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { BookingStatusAppointmentBadge } from "../../Badges/BookingStatusAppointmentBadge";
import { BookingStatusClassBadge } from "../../Badges/BookingStatusClassBadge";

type BookingItemProps = {
  type: "upcoming" | "past";
  previousDateISO?: string;
} & (
  | {
      as: "class";
      info: EventRsvpCustomerDTO;
    }
  | {
      as: "appointment";
      info: AppointmentCustomerDTO;
    }
);

export const BookingItem = ({
  info,
  previousDateISO,
  type,
  as,
}: BookingItemProps) => {
  const { t } = useTranslation();
  const { timezone } = useClubSettings();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const history = useHistory();

  const now = DateTime.now().setZone(timezone);
  const start = DateTime.fromISO(info.startDate).setZone(timezone);
  const end = DateTime.fromISO(info.endDate).setZone(timezone);

  const isLive = Interval.fromDateTimes(start, end).contains(now);

  const shouldShowDate = useMemo(() => {
    if (!previousDateISO) return true;
    const prev = DateTime.fromISO(previousDateISO).setZone(timezone);
    return !start.hasSame(prev, "day");
  }, [previousDateISO, start, timezone]);

  const displayDateLabel = useMemo(() => {
    if (!shouldShowDate) {
      return "";
    }
    if (start.hasSame(now, "day")) return t("portal.common.today");
    if (start.hasSame(now.plus({ days: 1 }), "day"))
      return t("portal.common.tomorrow");
    if (start.hasSame(now.minus({ days: 1 }), "day"))
      return t("portal.common.yesterday");
    return start.toLocaleString(DateTime.DATE_MED);
  }, [shouldShowDate, start, now, t]);

  return (
    <div className="flex w-full max-w-lg flex-col gap-3">
      {shouldShowDate && (
        <div className="text-sm font-semibold text-gray-500 dark:text-gray-400">
          {displayDateLabel}
        </div>
      )}
      <div
        onClick={() => {
          if (type === "past") {
            return;
          }

          const id = info.id.toString();

          history.push(
            createSiteOrEmbedLink(
              as === "class"
                ? RouteFeature.SiteTimetableId.replace(
                    ":viewType",
                    "calendar",
                  ).replace(":eventOccurrenceId", id)
                : RouteFeature.SiteProfileBookingAppointmentId.replace(
                    ":appointmentId",
                    id,
                  ),
            ),
          );
        }}
        className={cn(
          "shadow-xs bg-gray-0 relative flex w-full flex-col gap-1 rounded-2xl border border-gray-200 p-3 dark:border-gray-600 dark:bg-gray-950 hover:opacity-70 cursor-pointer",
          { "opacity-50 cursor-auto hover:opacity-50": type === "past" },
        )}
      >
        {isLive && (
          <div className="absolute inset-0 -top-1 flex rounded-full">
            <div className="flex flex-1 justify-center">
              <DotBadge intent="success">{t("portal.common.live")}</DotBadge>
            </div>
          </div>
        )}
        <div className="flex flex-row items-center justify-between">
          <div className="flex min-w-fit flex-row items-center gap-2">
            <div className="dark:text-gray-25 min-w-fit text-lg font-bold text-gray-950">
              {DateTime.fromISO(info.startDate)
                .setZone(timezone)
                .toLocaleString(DateTime.TIME_SIMPLE)}
            </div>
            <div className="flex flex-row items-center gap-1">
              <FontAwesomeIcon
                className="size-3.5 text-gray-500"
                icon={faClock}
              />
              <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
                {Interval.fromDateTimes(
                  DateTime.fromISO(info.startDate),
                  DateTime.fromISO(info.endDate),
                )
                  .toDuration()
                  .rescale()
                  .toHuman({
                    unitDisplay: "narrow",
                    listStyle: "short",
                  })}
              </div>
            </div>
          </div>
          {as === "class" ? (
            <BookingStatusClassBadge booking={info} />
          ) : (
            <BookingStatusAppointmentBadge appointment={info} />
          )}
        </div>
        <div className="dark:text-gray-25 text-sm font-medium text-gray-950">
          {as === "class" ? info.activityName : info.appointableName}
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="dark:text-gray-25 text-lg font-bold text-gray-950">
            {info.hostFirstName && info.hostLastName && (
              <StaffAvatar
                userStaff={{
                  firstName: info.hostFirstName,
                  lastName: info.hostLastName,
                  picture: info.hostPicture,
                }}
              />
            )}
          </div>
          {info.facilityName && (
            <div className="flex flex-row items-center gap-1">
              <LocationMarkerIcon pathClassName="stroke-gray-500 dark:stroke-gray-400" />
              <div className="dark:text-gray-25 line-clamp-1 text-xs font-medium text-gray-950">
                {info.facilityName}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
